import React, { useEffect, useState } from 'react';
import { File, Eye, Download } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import { PDFDocument } from 'pdf-lib';

interface Attachment {
    id: string;
    name: string;
    url: string;
    type?: string;
}

interface AttachmentItemProps {
    attachment: Attachment;
    onPreview: (url: string, name: string) => void;
    onDownload: (url: string, name: string) => void;
}

interface AttachmentsProps {
    attachments: Attachment[];
}

import 'pdfjs-dist/build/pdf.worker.min.mjs';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).href;

const AttachmentItem: React.FC<AttachmentItemProps> = ({
                                                           attachment,
                                                           onPreview,
                                                           onDownload
                                                       }) => {
    return (
        <div className="rounded-lg bg-white p-4 mb-4 shadow-sm">
            <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                    <div className="bg-gray-100 rounded-full p-3">
                        <File className="text-primary h-6 w-6" />
                    </div>
                    <div>
                        <h3 className="text-secondary font-medium">{attachment.name}</h3>
                        {attachment.type && (
                            <p className="text-gray-500 text-sm">{attachment.type}</p>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => onPreview(attachment.url, attachment.name)}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium text-sm inline-flex items-center gap-2"
                    >
                        <Eye className="h-4 w-4" />
                        Preview
                    </button>
                    <button
                        type="button"
                        onClick={() => onDownload(attachment.url, attachment.name)}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium text-sm inline-flex items-center gap-2"
                    >
                        <Download className="h-4 w-4" />
                        Download
                    </button>
                </div>
            </div>
        </div>
    );
};

export const Attachments: React.FC<AttachmentsProps> = ({ attachments }) => {
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string>('');
    const [previewType, setPreviewType] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [blobCache, setBlobCache] = useState<Map<string, string>>(new Map());

    const getFileTypeFromName = (name: string): string => {
        const extensionMatch = name.match(/\.([0-9a-z]+)$/i);
        const extension = extensionMatch ? extensionMatch[1].toLowerCase() : '';
        console.log(`[getFileTypeFromName] File name: ${name}, Detected extension: ${extension || 'none'}`);

        const mimeTypes: { [key: string]: string } = {
            'pdf': 'application/pdf',
            'jpg': 'image/jpeg',
            'jpeg': 'image FAILED TO LOADjpeg',
            'png': 'image/png',
            'gif': 'image/gif',
            'txt': 'text/plain',
            'doc': 'application/msword',
            'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'ppt': 'application/vnd.ms-powerpoint',
            'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            'xls': 'application/vnd.ms-excel',
            'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        };

        let detectedType = mimeTypes[extension] || 'application/octet-stream';

        if (!extension) {
            const lowerName = name.toLowerCase();
            if (lowerName.includes('tax clearance') || lowerName.includes('certificate') || lowerName.includes('document')) {
                detectedType = 'application/pdf';
                console.log(`[getFileTypeFromName] No extension found, assuming PDF based on name: ${name}`);
            } else if (lowerName.includes('photo') || lowerName.includes('image') || lowerName.includes('picture')) {
                detectedType = 'image/jpeg';
                console.log(`[getFileTypeFromName] No extension found, assuming JPG based on name: ${name}`);
            }
        }

        console.log(`[getFileTypeFromName] Detected MIME type: ${detectedType}`);
        return detectedType;
    };

    const handleDownload = (url: string, name: string) => {
        console.log(`[handleDownload] Downloading file: ${url}, Name: ${name}`);
        const link = document.createElement('a');
        link.href = url;
        link.download = name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        console.log('[handleDownload] Download triggered');
    };

    const cleanupBlob = (url: string) => {
        console.log(`[cleanupBlob] Cleaning up blob for URL: ${url}`);
        if (blobCache.has(url)) {
            URL.revokeObjectURL(blobCache.get(url)!);
            setBlobCache(prev => {
                const newCache = new Map(prev);
                newCache.delete(url);
                return newCache;
            });
            console.log(`[cleanupBlob] Blob URL revoked and removed from cache`);
        }
    };

    useEffect(() => {
        console.log('[useEffect] Component mounted');
        return () => {
            console.log('[useEffect] Component unmounting, cleaning up all blobs');
            blobCache.forEach((_, url) => cleanupBlob(url));
        };
    }, []);

    const handlePreview = async (url: string, name: string) => {
        console.log(`[handlePreview] Starting preview for URL: ${url}, Name: ${name}`);
        setIsLoading(true);
        setError(null);

        try {
            if (blobCache.has(url)) {
                console.log(`[handlePreview] Found in cache: ${url}`);
                setPreviewUrl(blobCache.get(url)!);
                setPreviewType(getFileTypeFromName(name));
                setIsPreviewOpen(true);
                setIsLoading(false);
                return;
            }

            console.log(`[handlePreview] Fetching file from URL: ${url}`);
            const response = await fetch(url, { method: 'GET', credentials: 'omit' });
            console.log(`[handlePreview] Response status: ${response.status}, Status text: ${response.statusText}`);
            console.log(`[handlePreview] Response headers:`, Object.fromEntries(response.headers.entries()));

            if (!response.ok) throw new Error(`Failed to download file: ${response.statusText}`);

            const contentType = response.headers.get('Content-Type') || 'unknown';
            console.log(`[handlePreview] Server-provided Content-Type: ${contentType}`);

            const blob = await response.blob();
            console.log(`[handlePreview] Blob type: ${blob.type}, Blob size: ${blob.size} bytes`);

            if (blob.size === 0) throw new Error('File appears to be empty');

            let finalBlob: Blob;
            const text = await blob.slice(0, 4).text();
            if (text === '%PDF') {
                console.log(`[handlePreview] File is already a PDF`);
                finalBlob = new Blob([blob], { type: 'application/pdf' });
            } else {
                console.log(`[handlePreview] File is not a PDF, determining type for conversion`);
                const detectedType = contentType !== 'application/octet-stream' && contentType !== 'unknown'
                    ? contentType
                    : getFileTypeFromName(name);
                console.log(`[handlePreview] Detected type: ${detectedType}`);

                const pdfDoc = await PDFDocument.create();
                const page = pdfDoc.addPage();

                if (detectedType.includes('image')) {
                    const arrayBuffer = await blob.arrayBuffer();
                    let image;
                    if (detectedType.includes('jpeg')) {
                        image = await pdfDoc.embedJpg(arrayBuffer);
                    } else if (detectedType.includes('png')) {
                        image = await pdfDoc.embedPng(arrayBuffer);
                    } else {
                        throw new Error(`Unsupported image type: ${detectedType}`);
                    }
                    const { width, height } = image.scaleToFit(595, 842);
                    page.drawImage(image, { x: (595 - width) / 2, y: (842 - height) / 2, width, height });
                } else if (detectedType.includes('text')) {
                    const textContent = await blob.text();
                    page.drawText(textContent, { x: 50, y: 792, size: 12, maxWidth: 495 });
                } else {
                    console.log(`[handlePreview] Detected type ${detectedType} not convertible, attempting as image`);
                    const arrayBuffer = await blob.arrayBuffer();
                    let image;
                    try {
                        image = await pdfDoc.embedJpg(arrayBuffer);
                    } catch (jpgErr: any) {
                        try {
                            image = await pdfDoc.embedPng(arrayBuffer);
                        } catch (pngErr: any) {
                            throw new Error(`File is not a valid PDF and cannot be converted: ${jpgErr.message}, ${pngErr.message}`);
                        }
                    }
                    const { width, height } = image.scaleToFit(595, 842);
                    page.drawImage(image, { x: (595 - width) / 2, y: (842 - height) / 2, width, height });
                }

                const pdfBytes = await pdfDoc.save();
                finalBlob = new Blob([pdfBytes], { type: 'application/pdf' });
                console.log(`[handlePreview] Converted to PDF, size: ${finalBlob.size} bytes`);
            }

            const localUrl = URL.createObjectURL(finalBlob);
            console.log(`[handlePreview] Created local URL: ${localUrl}`);

            setBlobCache(prev => new Map(prev).set(url, localUrl));
            setPreviewUrl(localUrl);
            setPreviewType('application/pdf');
            setIsPreviewOpen(true);
        } catch (err: any) {
            console.error('[handlePreview] Preview error:', err);
            setError(`Failed to load preview: ${err.message}. Please try downloading instead.`);
        } finally {
            setIsLoading(false);
            console.log('[handlePreview] Preview process completed');
        }
    };

    const renderPreviewContent = () => {
        console.log(`[renderPreviewContent] Rendering preview, Type: ${previewType}, URL: ${previewUrl}`);
        if (!isPreviewOpen) return null;

        if (isLoading) {
            console.log('[renderPreviewContent] Showing loading state');
            return (
                <div className="flex justify-center items-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            );
        }

        if (error) {
            console.log(`[renderPreviewContent] Showing error: ${error}`);
            return (
                <div className="text-center p-4">
                    <p className="text-red-500">{error}</p>
                    <button
                        onClick={() => handleDownload(previewUrl, attachments.find(a => a.url === previewUrl)?.name || 'document')}
                        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                        Download Instead
                    </button>
                </div>
            );
        }

        const type = previewType.toLowerCase();
        console.log(`[renderPreviewContent] Preview type (lowercase): ${type}`);

        if (type.includes('image/jpeg') || type.includes('image/png') || type.includes('image/gif')) {
            console.log('[renderPreviewContent] Rendering image preview');
            return (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <img
                        src={previewUrl}
                        alt="Image Preview"
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                            console.error('[renderPreviewContent] Image load error:', e);
                            setError('Failed to load image preview');
                        }}
                        onLoad={() => console.log('[renderPreviewContent] Image loaded successfully')}
                    />
                </div>
            );
        }

        if (type.includes('pdf')) {
            console.log('[renderPreviewContent] Rendering PDF preview with react-pdf');
            return (
                <div className="w-full h-full overflow-auto">
                    <Document
                        file={previewUrl}
                        onLoadSuccess={() => console.log('[renderPreviewContent] PDF loaded successfully with react-pdf')}
                        onLoadError={(e) => {
                            console.error('[renderPreviewContent] PDF load error with react-pdf:', e);
                            setError('Failed to load PDF preview');
                        }}
                    >
                        <Page pageNumber={1} />
                    </Document>
                </div>
            );
        }

        if (type.includes('msword') || type.includes('wordprocessingml')) {
            console.log('[renderPreviewContent] Rendering Word document preview');
            return (
                <iframe
                    src={previewUrl}
                    className="w-full h-full"
                    title="Word Document Preview"
                    sandbox="allow-same-origin allow-scripts allow-forms"
                    onError={(e) => {
                        console.error('[renderPreviewContent] Word document load error:', e);
                        setError('Failed to load Word document preview');
                    }}
                    onLoad={() => console.log('[renderPreviewContent] Word document loaded successfully')}
                />
            );
        }

        if (type.includes('powerpoint') || type.includes('presentationml')) {
            console.log('[renderPreviewContent] Rendering PowerPoint preview');
            return (
                <iframe
                    src={previewUrl}
                    className="w-full h-full"
                    title="PowerPoint Preview"
                    sandbox="allow-same-origin allow-scripts allow-forms"
                    onError={(e) => {
                        console.error('[renderPreviewContent] PowerPoint load error:', e);
                        setError('Failed to load PowerPoint preview');
                    }}
                    onLoad={() => console.log('[renderPreviewContent] PowerPoint loaded successfully')}
                />
            );
        }

        if (type.includes('text/plain')) {
            console.log('[renderPreviewContent] Rendering text preview');
            return (
                <iframe
                    src={previewUrl}
                    className="w-full h-full"
                    title="Text Preview"
                    sandbox="allow-same-origin"
                    onError={(e) => {
                        console.error('[renderPreviewContent] Text load error:', e);
                        setError('Failed to load text preview');
                    }}
                    onLoad={() => console.log('[renderPreviewContent] Text loaded successfully')}
                />
            );
        }

        console.log(`[renderPreviewContent] Fallback: Preview not available for type ${type}`);
        return (
            <div className="text-center p-4">
                <p>Preview not available for this file type ({type})</p>
                <button
                    onClick={() => handleDownload(previewUrl, attachments.find(a => a.url === previewUrl)?.name || 'document')}
                    className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                    Download to View
                </button>
            </div>
        );
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-secondary text-xl font-bold">Attachments</h2>
            </div>

            <div className="mt-6">
                {attachments.map(attachment => (
                    <AttachmentItem
                        key={attachment.id}
                        attachment={attachment}
                        onPreview={handlePreview}
                        onDownload={handleDownload}
                    />
                ))}
            </div>

            {isPreviewOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="w-full max-w-4xl h-[80vh] bg-white rounded-lg shadow-lg relative">
                        <button
                            onClick={() => {
                                console.log('[Preview] Closing preview');
                                setIsPreviewOpen(false);
                                cleanupBlob(previewUrl);
                                setPreviewUrl('');
                                setPreviewType('');
                                setError(null);
                            }}
                            className="absolute top-2 right-2 px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 z-10"
                        >
                            Close
                        </button>
                        <div className="w-full h-full p-4 overflow-auto">
                            {renderPreviewContent()}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};